import {
  isTelemetryStale,
  telemetryAgeMs,
  type TelemetryKey,
  type VehicleState,
} from '@hgc/core';
import type { LinkStats } from '@hgc/link';
import type { StatusLevel } from '@hgc/ui';

import { EM_DASH, ageText, coordinate, fixed, groundSpeed, integer, scaled } from './format';

/**
 * 一行读数。
 *
 * `note` 里放的是"为什么这个值不可信"（数据来自哪条消息、多久没更新），
 * 而不是复述这一行是什么 —— 那已经写在 `label` 里了。
 */
export interface MetricRow {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly unit: string;
  readonly note: string;
  readonly level: StatusLevel;
}

const LEVEL_ORDER: readonly StatusLevel[] = ['neutral', 'info', 'ok', 'caution', 'warning', 'critical'];

/** 取更严重的那个等级：一条读数既是"过期"又是"电池告急"时，不能显示成过期 */
function worstLevel(a: StatusLevel, b: StatusLevel): StatusLevel {
  return LEVEL_ORDER.indexOf(a) >= LEVEL_ORDER.indexOf(b) ? a : b;
}

interface RowSpec {
  readonly id: string;
  readonly label: string;
  readonly unit: string;
  /** 这一读数来自哪条 MAVLink 消息。排障时"这数字是谁说的"和数字本身一样重要 */
  readonly source: string;
  readonly key: TelemetryKey;
  /** `null` = 这一项现在没有可用值（没收到过 / 值不可用），与"值是 0"是两件事 */
  readonly read: (state: VehicleState) => string | null;
  readonly note?: (state: VehicleState) => string;
  readonly level?: (state: VehicleState) => StatusLevel;
}

const batteryLevel = (state: VehicleState): StatusLevel => {
  const remaining = state.remainingPct;
  if (remaining === null) return 'neutral';
  if (remaining <= 10) return 'critical';
  if (remaining <= 25) return 'warning';
  return 'ok';
};

const satelliteLevel = (state: VehicleState): StatusLevel => {
  const satellites = state.satellites;
  // 6 颗是 ArduPilot 默认的解锁门限：低于它连解锁都会被拒，值得提前变黄
  if (satellites === null) return 'neutral';
  return satellites < 6 ? 'warning' : 'ok';
};

const ROW_SPECS: readonly RowSpec[] = [
  // --- 姿态 ---
  { id: 'roll', label: '姿态 · 滚转', unit: '°', source: 'ATTITUDE', key: 'attitude', read: (s) => fixed(s.rollDeg, 1) },
  { id: 'pitch', label: '姿态 · 俯仰', unit: '°', source: 'ATTITUDE', key: 'attitude', read: (s) => fixed(s.pitchDeg, 1) },
  { id: 'yaw', label: '姿态 · 偏航', unit: '°', source: 'ATTITUDE', key: 'attitude', read: (s) => fixed(s.yawDeg, 1) },

  // --- 位置 ---
  { id: 'lat', label: '位置 · 纬度', unit: '°', source: 'GLOBAL_POSITION_INT', key: 'position', read: (s) => coordinate(s.latDeg) },
  { id: 'lon', label: '位置 · 经度', unit: '°', source: 'GLOBAL_POSITION_INT', key: 'position', read: (s) => coordinate(s.lonDeg) },
  { id: 'alt', label: '位置 · 海拔', unit: 'm', source: 'GLOBAL_POSITION_INT', key: 'position', read: (s) => fixed(s.altM, 1) },
  { id: 'rel-alt', label: '位置 · 相对高度', unit: 'm', source: 'GLOBAL_POSITION_INT', key: 'position', read: (s) => fixed(s.relativeAltM, 1) },
  { id: 'heading', label: '位置 · 航向', unit: '°', source: 'GLOBAL_POSITION_INT', key: 'position', read: (s) => fixed(s.headingDeg, 0) },
  { id: 'ground-speed', label: '位置 · 地速', unit: 'm/s', source: 'GLOBAL_POSITION_INT', key: 'position', read: (s) => groundSpeed(s.vxMps, s.vyMps) },

  // --- GPS ---
  {
    id: 'fix',
    label: 'GPS · 定位类型',
    unit: '',
    source: 'GPS_RAW_INT',
    key: 'gps',
    read: (s) => s.fixTypeName?.replace('GPS_FIX_TYPE_', '') ?? (s.fixType === null ? null : `类型 ${s.fixType}`),
  },
  {
    id: 'satellites',
    label: 'GPS · 可见卫星',
    unit: '颗',
    source: 'GPS_RAW_INT',
    key: 'gps',
    read: (s) => integer(s.satellites),
    level: satelliteLevel,
  },
  { id: 'hdop', label: 'GPS · 水平精度', unit: 'HDOP', source: 'GPS_RAW_INT', key: 'gps', read: (s) => fixed(s.hdop, 2) },

  // --- 电池 ---
  { id: 'voltage', label: '电池 · 电压', unit: 'V', source: 'SYS_STATUS', key: 'battery', read: (s) => fixed(s.voltageV, 2) },
  { id: 'current', label: '电池 · 电流', unit: 'A', source: 'SYS_STATUS', key: 'battery', read: (s) => fixed(s.currentA, 2) },
  {
    id: 'remaining',
    label: '电池 · 剩余',
    unit: '%',
    source: 'BATTERY_STATUS',
    key: 'battery',
    read: (s) => integer(s.remainingPct),
    level: batteryLevel,
  },

  // --- 估计器与振动 ---
  {
    id: 'ekf',
    label: 'EKF · 状态',
    unit: '',
    source: 'EKF_STATUS_REPORT',
    key: 'ekf',
    read: (s) => (s.ekfHealthy === null ? null : s.ekfHealthy ? '正常' : '异常'),
    note: (s) => (s.ekfFlags === null ? '' : `flags 0x${s.ekfFlags.toString(16)}`),
    level: (s) => (s.ekfHealthy === false ? 'critical' : s.ekfHealthy === true ? 'ok' : 'neutral'),
  },
  { id: 'ekf-vel', label: 'EKF · 速度方差', unit: '', source: 'EKF_STATUS_REPORT', key: 'ekf', read: (s) => fixed(s.ekfVelocityVariance, 3) },
  { id: 'ekf-pos-h', label: 'EKF · 水平位置方差', unit: '', source: 'EKF_STATUS_REPORT', key: 'ekf', read: (s) => fixed(s.ekfPosHorizVariance, 3) },
  { id: 'ekf-pos-v', label: 'EKF · 垂直位置方差', unit: '', source: 'EKF_STATUS_REPORT', key: 'ekf', read: (s) => fixed(s.ekfPosVertVariance, 3) },
  { id: 'vibration', label: '振动 · X / Y / Z', unit: 'm/s²', source: 'VIBRATION', key: 'vibration', read: (s) => (s.vibration === null ? null : `${fixed(s.vibration.x, 1)} / ${fixed(s.vibration.y, 1)} / ${fixed(s.vibration.z, 1)}`) },
  { id: 'vibration-clipping', label: '振动 · 削波次数', unit: '', source: 'VIBRATION', key: 'vibration', read: (s) => integer(s.vibration?.clipping ?? null) },

  // --- 飞行 ---
  {
    id: 'armed',
    label: '飞行 · 解锁状态',
    unit: '',
    source: 'HEARTBEAT',
    key: 'heartbeat',
    read: (s) => (s.armed ? '已解锁' : '未解锁'),
    level: (s) => (s.armed ? 'caution' : 'neutral'),
  },
  {
    id: 'mode',
    label: '飞行 · 模式编号',
    unit: '',
    source: 'HEARTBEAT',
    key: 'heartbeat',
    read: (s) => s.modeName,
    note: () => '只显示编号：模式名表（@hgc/ardupilot）还没接进来',
  },
  {
    id: 'aircraft',
    label: '飞行 · 机型',
    unit: '',
    source: 'HEARTBEAT',
    key: 'heartbeat',
    read: (s) => s.typeName,
    note: (s) => (s.isAutopilot === null ? '' : s.isAutopilot ? '飞控' : '非飞控组件'),
  },
  { id: 'airspeed', label: '飞行 · 空速', unit: 'm/s', source: 'VFR_HUD', key: 'hud', read: (s) => fixed(s.airspeedMps, 1) },
  { id: 'climb', label: '飞行 · 爬升率', unit: 'm/s', source: 'VFR_HUD', key: 'hud', read: (s) => fixed(s.climbRateMps, 2) },
  { id: 'throttle', label: '飞行 · 油门', unit: '%', source: 'VFR_HUD', key: 'hud', read: (s) => integer(s.throttlePct) },

  // --- 任务 ---
  {
    id: 'mission',
    label: '任务 · 当前航点',
    unit: '',
    source: 'MISSION_CURRENT',
    key: 'mission',
    read: (s) => (s.missionCurrentSeq === null ? null : String(s.missionCurrentSeq)),
    note: (s) => (s.missionTotal === null ? '' : `共 ${s.missionTotal} 个航点`),
  },
];

/**
 * 把一份车辆状态快照摊平成可显示的行。
 *
 * 三件事在这里发生，且只在这里发生：
 *   1. `null` → `—`。没有数据与"值是 0"必须显示得不一样；
 *   2. 逐子系统的**新鲜度**判断。姿态 1 秒、电池 15 秒、返航点 1 分钟，用一个全局
 *      时间戳无法同时表达（见 `TELEMETRY_STALE_MS`）；
 *   3. 过期时把等级抬高到 `caution`：一个不再更新的数字比没有数字更危险，
 *      因为它看起来是对的。
 */
export function buildTelemetryRows(state: VehicleState, now: number): readonly MetricRow[] {
  return ROW_SPECS.map((spec) => {
    const age = telemetryAgeMs(state, spec.key, now);
    const stale = isTelemetryStale(state, spec.key, now);
    const raw = spec.read(state);

    let note = spec.note?.(state) ?? '';
    if (age === null) note = `还没收到过 ${spec.source}`;
    else if (stale) note = `过期：${ageText(age)}更新（${spec.source}）`;
    else if (note === '') note = `来自 ${spec.source}`;

    const base = spec.level?.(state) ?? 'neutral';
    return Object.freeze({
      id: spec.id,
      label: spec.label,
      value: raw ?? EM_DASH,
      unit: raw === null ? '' : spec.unit,
      note,
      level: stale && raw !== null ? worstLevel(base, 'caution') : base,
    });
  });
}

/**
 * 链路统计的行。
 *
 * 与遥测读数分开是因为它们的时间尺度完全不同：遥测是"机体现在怎么样"，
 * 链路统计是"这条线好不好"。把两者混在一张表里，会让"CRC 错误开始涨了"
 * 淹没在姿态数值里。
 */
export function buildLinkStatRows(stats: LinkStats): readonly MetricRow[] {
  const errorRow = (
    id: string,
    label: string,
    count: number,
    level: StatusLevel,
  ): MetricRow => ({
    id,
    label,
    value: String(count),
    unit: '',
    note: count === 0 ? '没有发生' : '这不该持续增长；增长说明链路质量有问题',
    level: count === 0 ? 'neutral' : level,
  });

  return [
    { id: 'frames-in', label: '收到帧数', value: String(stats.framesIn), unit: '', note: '本会话累计', level: 'neutral' },
    { id: 'frames-out', label: '发出帧数', value: String(stats.framesOut), unit: '', note: '本会话累计', level: 'neutral' },
    { id: 'bytes-in', label: '收到字节', value: scaled(stats.bytesIn, 1024, 1), unit: 'KiB', note: '本会话累计', level: 'neutral' },
    { id: 'bytes-out', label: '发送字节', value: scaled(stats.bytesOut, 1024, 1), unit: 'KiB', note: '本会话累计', level: 'neutral' },
    { id: 'rate', label: '收帧速率', value: fixed(stats.framesPerSecond, 1), unit: 'Hz', note: '最近一秒', level: 'neutral' },
    { id: 'throughput', label: '收字节速率', value: scaled(stats.bytesPerSecond, 1024, 1), unit: 'KiB/s', note: '最近一秒', level: 'neutral' },
    {
      id: 'silence',
      label: '静默时长',
      value: stats.silenceMs === null ? EM_DASH : fixed(stats.silenceMs / 1000, 1),
      unit: 's',
      note: stats.silenceMs === null ? '还没有收到过任何帧' : `最后收帧：${ageText(stats.silenceMs)}`,
      // 心跳判定是 3 秒；超过它就已经算断链，不该等到界面自己察觉
      level: stats.silenceMs !== null && stats.silenceMs > 3000 ? 'warning' : 'neutral',
    },
    errorRow('crc', 'CRC 错误', stats.crcErrors, 'warning'),
    errorRow('malformed', '畸形帧', stats.malformedFrames, 'warning'),
    errorRow('dropped', '丢弃字节', stats.droppedBytes, 'caution'),
    errorRow('unknown', '未知消息 ID', stats.unknownMessages, 'info'),
    errorRow('signature', '签名校验失败', stats.signatureFailures, 'critical'),
  ];
}
